import React, { useState } from 'react';
import { Container } from '../../components/Container/Container';
import { t } from '../../locales/translate';
import { Card } from '../../components/Card/Card';
import { SortableList } from '../../components/SortableList/SortableList';
import { Stack } from '../../components/Stack/Stack';
import { graphql } from '../../generated';
import { CursorPagination, RowsPerPage } from '../../components/cursor_pagination/cursor_pagination';
import { useQueryHandler } from '../../model/graphql';
import { Export } from '../../generated/graphql';
import { useReloadKey } from '../../util/react-util';
import { formatDateTime } from '../../util/formatters';
import { Button } from '../../components/Button/Button';
import { Icon } from '../../components/Icon/Icon';
import { FileUploadModal } from '../../components/file_upload_modal/file_upload_modal';
import { runPhysicalSalesChartExport } from '../../model/exports';
import { useModalPresenter } from '../../components/Modal/PresentedModal';
import { handleSuccess } from '../../model/notification';
import { Preview } from './preview';
import { Tag } from '../../components/Tag/Tag';
import { ConfirmResolvedErrors } from './confirm_resolved_errors';

const CHARTS_EXPORTS = graphql(`
  query PhysicalSalesCharts($first: Int, $cursor: String) {
    exports(types: [physical_sales_chart, charts, book_charts], first: $first, after: $cursor) {
      edges {
        node {
          id
          url
          createdAt
          type
          filename
          error
          params
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
      }
    }
  }
`);

export function Charts(): JSX.Element {
  const modal = useModalPresenter();
  const [rows, setRows] = useState<RowsPerPage>('10');
  const [reloadKey, reloadCharts] = useReloadKey();
  const { data, loading, fetchMore, refetch } = useQueryHandler(CHARTS_EXPORTS, {
    variables: {
      first: parseInt(rows),
      cursor: null,
    },
    reloadKey,
  });
  const pageInfo = data?.exports?.pageInfo;

  return (
    <Container>
      <Stack size="lg">
        <h1 className="bold">{t.model.charts._name}</h1>
        <Card
          fullbleed
          title={t.model.charts.reports}
          actions={
            <>
              <Icon
                name="eye"
                tooltip={t.model.charts.preview}
                onClick={() => {
                  modal.present(Preview, {
                    onCreated: () => {
                      reloadCharts();
                    },
                  });
                }}
              />
              <Icon
                name="plus"
                onClick={() => {
                  modal.present(FileUploadModal, {
                    title: t.model.charts.uploadChart,
                    submit: (file) => runPhysicalSalesChartExport(file),
                    onUploadComplete: () => {
                      reloadCharts();
                      handleSuccess({ title: t.model.charts.uploadSuccess, message: t.model.charts.uploadSuccessful });
                    },
                  });
                }}
                tooltip={t.model.charts.upload}
              />
            </>
          }
          pagination={
            <CursorPagination
              hasNextPage={pageInfo?.hasNextPage}
              hasPreviousPage={pageInfo?.hasPreviousPage}
              rowsPerPage={rows}
              onRowsPerPageChange={setRows}
              onFirstPageClick={refetch}
              onNextPageClick={() => {
                fetchMore({
                  variables: {
                    cursor: pageInfo?.endCursor,
                  },
                });
              }}
            />
          }
        >
          <SortableList
            size="default"
            items={data?.exports?.edges?.map((edge) => edge?.node) as Export[]}
            loading={loading}
            columns={[
              {
                width: 200,
                label: t.model.charts.date,
                sortable: false,
                dataKey: 'createdAt',
                cellRenderer: ({ createdAt }) => formatDateTime(createdAt),
              },
              {
                width: 200,
                label: 'type',
                sortable: false,
                dataKey: 'type',
                cellRenderer: ({ type }) => type,
              },
              {
                label: t.model.charts.fileName,
                sortable: false,
                dataKey: 'filename',
                cellRenderer: ({ url, filename, error, params, type, id }) => (
                  <Stack size="xxs">
                    <Stack line>
                      {filename && (
                        <Button
                          type="link"
                          title={filename ?? undefined}
                          disabled={!url}
                          onClick={() => url && window.open(url, '_blank')}
                        />
                      )}
                      {params['preview'] && <Tag text={t.model.charts.preview} />}
                      {type === 'PhysicalSalesChartExport' && <Tag text={t.model.charts.manualUploaded} />}
                    </Stack>
                    {error && (
                      <Stack line size="xxs">
                        <Icon name="alert-circle--sm" />
                        {error}
                        <Icon
                          name="remove-circle"
                          color="var(--color-warning)"
                          tooltip={t.model.charts.markAsResolved}
                          onClick={() => {
                            modal.present(
                              ConfirmResolvedErrors,
                              {
                                onUpdate: () => {
                                  reloadCharts();
                                },
                                id,
                              },
                              { size: 'small' }
                            );
                          }}
                        />
                      </Stack>
                    )}
                  </Stack>
                ),
              },
            ]}
          />
        </Card>
      </Stack>
    </Container>
  );
}
